/**
 * BackgroundJobDetailsModal Component
 *
 * Displays detailed information about a background job.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { useBackgroundJobDetails } from '../hooks/useBackgroundJobDetails';
import type { JobStatus } from '../../../types/background-job.types';

interface BackgroundJobDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  jobId: string;
}

/**
 * Get badge variant based on job status
 */
function getStatusBadgeVariant(status: JobStatus): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'running':
      return 'warning';
    case 'failed':
      return 'error';
    case 'pending':
    default:
      return 'default';
  }
}

/**
 * Get human-readable status label
 */
function getStatusLabel(status: JobStatus): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'running':
      return 'Running';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'Pending';
    default:
      return status;
  }
}

/**
 * Format duration between two timestamps
 */
function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '-';

  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const durationMs = end - start;

  if (durationMs < 1000) return `${durationMs}ms`;
  if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
  if (durationMs < 3600000) return `${Math.floor(durationMs / 60000)}m ${Math.floor((durationMs % 60000) / 1000)}s`;
  return `${Math.floor(durationMs / 3600000)}h ${Math.floor((durationMs % 3600000) / 60000)}m`;
}

/**
 * Format timestamp to human-readable
 */
function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString();
}

/**
 * Format timeout in human-readable format
 */
function formatTimeout(timeoutMs: number): string {
  if (timeoutMs < 1000) return `${timeoutMs}ms`;
  if (timeoutMs < 60000) return `${timeoutMs / 1000}s`;
  return `${timeoutMs / 60000} minutes`;
}

export const BackgroundJobDetailsModal: React.FC<BackgroundJobDetailsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  jobId,
}) => {
  // Auto-refresh for running jobs
  const { data: job, isLoading, error } = useBackgroundJobDetails(tenantId, jobId, {
    enabled: isOpen,
    refetchInterval: (data) =>
      data?.status === 'running' || data?.status === 'pending' ? 3000 : false,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header title="Background Job Details" />
      <Modal.Body>
        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        )}

        {error && <Alert variant="error">{error.message}</Alert>}

        {job && (
          <div className="space-y-6">
            {/* Status Banner */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <Badge variant={getStatusBadgeVariant(job.status)} size="lg">
                {getStatusLabel(job.status)}
              </Badge>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Duration</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatDuration(job.startedAt, job.completedAt)}
              </p>
            </div>
          </div>

          {/* Error Alert */}
          {job.error && (
            <Alert variant="error" title={`Error: ${job.error.code}`}>
              {job.error.message}
            </Alert>
          )}

          {/* Job Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Job ID</p>
              <p className="mt-1 text-sm text-gray-900 font-mono break-all">{job.jobId}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Function</p>
              <p className="mt-1 text-sm text-gray-900 font-medium">{job.functionName}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Application</p>
              <p className="mt-1 text-sm text-gray-900">{job.applicationCode}</p>
            </div>
            {job.timeout !== undefined && (
              <div>
                <p className="text-sm font-medium text-gray-500">Timeout</p>
                <p className="mt-1 text-sm text-gray-900">{formatTimeout(job.timeout)}</p>
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Timestamps</h4>
            <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="text-xs text-gray-500">Created</p>
                <p className="text-sm text-gray-900">{formatTimestamp(job.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Started</p>
                <p className="text-sm text-gray-900">{formatTimestamp(job.startedAt)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Completed</p>
                <p className="text-sm text-gray-900">{formatTimestamp(job.completedAt)}</p>
              </div>
            </div>
          </div>

          {/* Auth Context */}
          {job.authContext && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Execution Context</h4>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Trigger Type</p>
                    <p className="text-sm text-gray-900 capitalize">{job.authContext.type.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Initiator Type</p>
                    <p className="text-sm text-gray-900 capitalize">{job.authContext.initiatorType.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Initiator</p>
                    <p className="text-sm text-gray-900">{job.authContext.initiatorName || job.authContext.initiatorId}</p>
                  </div>
                  {job.authContext.sourceApplicationCode && (
                    <div>
                      <p className="text-xs text-gray-500">Source Application</p>
                      <p className="text-sm text-gray-900">{job.authContext.sourceApplicationCode}</p>
                    </div>
                  )}
                  {job.authContext.scheduleName && (
                    <div>
                      <p className="text-xs text-gray-500">Schedule</p>
                      <p className="text-sm text-gray-900">{job.authContext.scheduleName}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payload */}
          {job.payload && Object.keys(job.payload).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Payload</h4>
              <pre className="bg-gray-800 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                {JSON.stringify(job.payload, null, 2)}
              </pre>
            </div>
          )}

          {/* Idempotency Key */}
          {job.idempotencyKey && (
            <div>
              <p className="text-sm font-medium text-gray-500">Idempotency Key</p>
              <p className="mt-1 text-sm text-gray-900 font-mono">{job.idempotencyKey}</p>
            </div>
          )}
        </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BackgroundJobDetailsModal;
